import {
  DropRectangle,
  ERectangleIdentifiers,
  RMLine,
  RMRectangle,
} from 'business/models/CustomModels';
import React, { useCallback } from 'react';
import { Shape } from 'react-konva';
import CalculateWellRects from './WellPainter';
import { generateWellTooltipText } from '../wellIngredientView/utils';
import { ContainerDrop, Drop, WellIng } from 'components/Experiment/constants';
import { PrecisionFormatter } from '../constants';

interface Props {
  wellRectangle: RMRectangle;
  wellData: WellIng; //well with ingredients
  maxWellVolume?: number;
  dropsList?: ContainerDrop[] | Drop[];
  plateNumber: number;
  wellPadding?: number;
  wellPositionDetails?: { x: number; y: number; colPos: number; rowPos: number };
  shouldShowIngredients?: boolean;
  strokeNormalizer: number;
  shouldBlurWell?: boolean;
  formatWithPrecision: PrecisionFormatter;
}

const WellDesignRenderer = (props: Props) => {
  const {
    wellRectangle,
    wellData,
    maxWellVolume,
    dropsList,
    plateNumber,
    wellPadding,
    wellPositionDetails,
    shouldShowIngredients,
    strokeNormalizer,
    shouldBlurWell,
    formatWithPrecision,
  } = props;

  const wellRects = CalculateWellRects(
    wellData,
    wellRectangle,
    maxWellVolume,
    dropsList,
    strokeNormalizer,
  );
  if (!wellRects) {
    return null;
  }
  const drawFunc = useCallback(
    (context, shape) => {
      if (shouldShowIngredients) {
        wellRects.drop.forEach((dropRects) => {
          const _rects = [];
          const _lines = [];
          dropRects.forEach((item) => {
            //RFR: need to intrduce a type property in class/interface that would simplify the type checking.
            //instead of checking if 'startX' exists, so that we infer its a line instead of a rectangle.
            if (Object.hasOwn(item, 'startX')) {
              _lines.push(item);
            } else {
              _rects.push(item);
            }
          });

          drawRects(context, shape, _rects, shouldBlurWell);
          drawLines(context, shape, _lines);
        });
        // draw drops
        //draw ingredients
        const _rects = [];
        const _lines = [];
        wellRects.ing.forEach((item) => {
          if (Object.hasOwn(item, 'startX')) {
            _lines.push(item);
          } else {
            _rects.push(item);
          }
        });
        drawRects(context, shape, _rects, shouldBlurWell);
        drawLines(context, shape, _lines);
      }

      //draw wells
      drawRects(context, shape, wellRects.wells, shouldBlurWell);

      context.fillStrokeShape(shape);
    },
    [wellRects],
  );

  const hitFunc = useCallback(
    (context, shape) => {
      context.beginPath();
      context.rect(
        wellPadding / 2,
        wellPadding / 2,
        wellRectangle.w - wellPadding,
        wellRectangle.h - wellPadding,
      );
      context.closePath();
      context.fillStrokeShape(shape);
    },
    [wellRectangle],
  );
  const dropsParentRects = wellRects?.drop?.map((dropRects) =>
    dropRects.find((r) => (r as DropRectangle).identifier === ERectangleIdentifiers.Drop),
  );
  return (
    <Shape
      sceneFunc={drawFunc}
      hitFunc={hitFunc}
      width={wellRectangle.w}
      height={wellRectangle.h}
      x={wellRectangle.x}
      y={wellRectangle.y}
      tooltipInfo={generateWellTooltipText(wellData, formatWithPrecision)}
      wellNum={wellData.wellNumber}
      plateNum={plateNumber}
      wellPositionDetails={wellPositionDetails}
      ingredientDetails={wellRects?.ing}
      drops={dropsParentRects}
      hasError={wellData.hasError}
    />
  );
};

const drawRects = (
  context,
  shape,
  drawData: RMRectangle[],
  blurWell: boolean = false,
) => {
  drawData.forEach((rect, index) => {
    if (!rect) {
      return;
    }

    if (index === 0 && blurWell) {
      context.globalAlpha = 0.8;
      context.fillStyle = '#DBF0F9';
    } else {
      context.globalAlpha = rect.opacity || 1;
      context.fillStyle = rect.fillColor || 'transparent';
    }
    context.strokeStyle = rect.bColor || 'transparent';
    context.lineWidth = rect.strokeWidth || 0.35;

    const rectWidth = rect.w;
    const rectHeight = rect.h;
    const x = rect.x - shape.attrs.x;
    const y = rect.y - shape.attrs.y;

    if (context.fillStyle !== 'transparent') {
      context.fillRect(x, y, rectWidth, rectHeight);
    }

    if (context.strokeStyle !== 'transparent') {
      context.strokeRect(x, y, rectWidth, rectHeight);
    }

    context.globalAlpha = 1;
  });
};

const drawLines = (context, shape, drawData: RMLine[]) => {
  if (!drawData || !drawData.length) {
    return;
  }

  drawData.forEach((line) => {
    if (!line) {
      return;
    }
    context.globalAlpha = line.opacity || 1;
    context.strokeStyle = line.bColor || 'black';
    context.lineWidth = line.strokeWidth || 0.35;

    const startX = line.startX - shape.attrs.x;
    const startY = line.startY - shape.attrs.y;
    const endX = line.endX - shape.attrs.x;
    const endY = line.endY - shape.attrs.y;

    context.beginPath();
    context.moveTo(startX, startY);
    context.lineTo(endX, endY);

    if (context.strokeStyle !== 'transparent') {
      context.stroke();
    }

    context.globalAlpha = 1;
  });
};

export default WellDesignRenderer;
